import React, {useCallback, useMemo, useState} from 'react';
import type {
  GetPersonnelSelectOptionsRequest} from 'src/api/ProactApiClient';
import {
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import type {
  AutocompleteProps} from 'src/design/atoms/Autocomplete';
import {
  Autocomplete
} from 'src/design/atoms/Autocomplete';
import {useGetPersonnelSelectOptionsQuery} from 'src/redux/queries/personnel';
import useDebounce from 'src/shared/hooks/useDebounce';
import type {ISelectOption} from 'src/types/Form';

export interface PersonSelectBoxProps
  extends Omit<AutocompleteProps, 'options' | 'value'> {
  queryOptions?: Partial<GetPersonnelSelectOptionsRequest>;
  value?: string;
  initialOption?: ISelectOption | null;
}
export const PersonSelectBox: React.FC<PersonSelectBoxProps> = React.memo(
  ({queryOptions, value, initialOption, ...other}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const debouncedSearchTerm = useDebounce(searchTerm, 400);

    const {data, isFetching, isLoading} = useGetPersonnelSelectOptionsQuery({
      ...queryOptions,
      start: 0,
      length: 50,
      search: debouncedSearchTerm || queryOptions?.search,
      active: queryOptions?.active ?? [UserStatusEnum.Active],
      userTypes: queryOptions?.userTypes ?? [
        UserTypeEnum.Contractor,
        UserTypeEnum.Employee,
      ],
      superviserOnly: queryOptions?.superviserOnly ?? false,
    });

    const options = useMemo(
      () => (data?.data ?? []) as ISelectOption[],
      [data?.data]
    );
    const selectedOption = useMemo(
      () =>
        options?.find(i => String(i.id) === String(value)) ??
        initialOption ??
        null,
      [options, value, initialOption]
    );

    const handleInputChange = useCallback(
      (event: React.SyntheticEvent, newInputValue: string) => {
        setSearchTerm(newInputValue);
      },
      []
    );

    const isSearching = isFetching && debouncedSearchTerm !== '';

    return (
      <Autocomplete
        {...other}
        disabled={isLoading || other.disabled}
        options={options}
        value={selectedOption}
        onInputChange={handleInputChange}
        loading={isSearching}
      />
    );
  }
);
